import { svgFileUrlPattern } from "./regex";

export type ProductImageInput = Partial<{
  svg_image: string;
  webp_image: string;
  png_image: string;
  svg_featured_image: string;
  webp_featured_image: string;
  png_featured_image: string;
  svg_cover: string;
  png_cover: string;
}>;

export type ProductImageSources = {
  src: string;
  secondSrcImg?: string;
  fallbackSrcs: string[];
  chain: string[];
};

type ImageFields = {
  svg?: string | null;
  webp?: string | null;
  png?: string | null;
};

const trimUrl = (value?: string | null) => value?.trim() || undefined;

const uniqueUrls = (urls: Array<string | undefined>) => {
  const seen = new Set<string>();
  const chain: string[] = [];

  for (const url of urls) {
    if (!url || seen.has(url)) continue;
    seen.add(url);
    chain.push(url);
  }

  return chain;
};

export const productImageUrls = (
  svg?: string | null,
  webp?: string | null,
  png?: string | null
) => uniqueUrls([trimUrl(svg), trimUrl(webp), trimUrl(png)]);

const urlsFor = ({ svg, webp, png }: ImageFields) =>
  productImageUrls(svg, webp, png);

const toSources = (chain: string[]): ProductImageSources => {
  const fallbackSrcs = chain.slice(1);

  return {
    src: chain[0] ?? "",
    secondSrcImg: fallbackSrcs[0],
    fallbackSrcs,
    chain,
  };
};

const thumbnailFields = (input: ProductImageInput): ImageFields => ({
  svg: input.svg_image,
  webp: input.webp_image,
  png: input.png_image,
});

const featuredFields = (input: ProductImageInput): ImageFields => ({
  svg: input.svg_featured_image,
  webp: input.webp_featured_image,
  png: input.png_featured_image,
});

export const resolveThumbnailSources = (
  input: ProductImageInput
): ProductImageSources => toSources(urlsFor(thumbnailFields(input)));

export const resolveFeaturedThenThumbnailSources = (
  input: ProductImageInput
): ProductImageSources => {
  const featured = urlsFor(featuredFields(input));
  const thumbnail = urlsFor(thumbnailFields(input));

  if (featured.length)
    return toSources(uniqueUrls([...featured, ...thumbnail]));
  return toSources(thumbnail);
};

export const resolveCartLineImageSources = (
  item: ProductImageInput & { image?: string }
): ProductImageSources => {
  const hasStructuredImage = Boolean(
    item.svg_image ||
      item.webp_image ||
      item.png_image ||
      item.svg_featured_image ||
      item.webp_featured_image ||
      item.png_featured_image
  );

  if (hasStructuredImage) {
    return resolveFeaturedThenThumbnailSources(item);
  }

  const image = trimUrl(item.image);
  if (!image) return toSources([]);

  const field = svgFileUrlPattern.test(image) ? "svg_image" : "png_image";
  return resolveThumbnailSources({ [field]: image });
};

export const resolveProductImageSources = (
  input: ProductImageInput
): ProductImageSources => {
  const thumbnail = urlsFor(thumbnailFields(input));
  const featured = urlsFor(featuredFields(input));
  const cover = urlsFor({ svg: input.svg_cover, png: input.png_cover });

  if (featured.length)
    return toSources(uniqueUrls([...featured, ...thumbnail]));
  if (cover.length) return toSources(uniqueUrls([...cover, ...thumbnail]));
  return toSources(thumbnail);
};
